import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { MainLayoutContainer } from "components/Layout";
import {
  useAddPostMutation,
  useGetPostByIdQuery,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";
import { VETCOIN_REWARDS, useVetcoin } from "hooks/useVetcoin";
import { TAG_COLOR } from "helpers/constants";
import { FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { AdminRoutes } from "config/routes";
import { audienceRoleLabel } from "./audienceRoleLabel";
import { PostAuthor } from "./PostAuthor";

type DateSort = "newest" | "oldest";

export const PostDetail: FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const { awardVetcoins } = useVetcoin(user?.user_id);
  const [answerBody, setAnswerBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateSort, setDateSort] = useState<DateSort>("oldest");

  const { data, loading, refetch } = useGetPostByIdQuery({
    variables: { postId: postId || "" },
    skip: !postId,
    fetchPolicy: "cache-and-network",
  });

  const [addPost] = useAddPostMutation();
  const post = data?.post_by_pk;

  const answers = useMemo(() => {
    const list = [...(post?.answers || [])];
    list.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return dateSort === "newest" ? bTime - aTime : aTime - bTime;
    });
    return list;
  }, [post?.answers, dateSort]);

  const handleAnswer = async () => {
    if (!post || !user?.user_id || !answerBody.trim()) {
      setError("Comment cannot be empty.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await addPost({
        variables: {
          object: {
            org_id: post.org_id,
            author_id: user.user_id,
            parent_post_id: post.post_id,
            title: null,
            body: answerBody.trim(),
          },
        },
      });
      const answerId = result.data?.insert_post_one?.post_id;
      if (answerId) {
        await awardVetcoins({
          userId: user.user_id,
          amount: VETCOIN_REWARDS.POST_ANSWER_CREATED,
          actionType: "POST_ANSWER_CREATED",
          sourcePostId: answerId,
        });
      }
      setAnswerBody("");
      await refetch();
    } catch (e: any) {
      setError(e?.message || "Could not post comment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !post) {
    return (
      <MainLayoutContainer title="Vet Discussions">
        <Typography>Loading...</Typography>
      </MainLayoutContainer>
    );
  }

  if (!post || post.parent_post_id) {
    return (
      <MainLayoutContainer title="Vet Discussions">
        <Alert severity="warning">Post not found.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(AdminRoutes.Posts)}>
          Back to Vet Discussions
        </Button>
      </MainLayoutContainer>
    );
  }

  return (
    <MainLayoutContainer
      title={post.title || "Vet Discussions"}
      buttonActions={
        <Button variant="outlined" onClick={() => navigate(AdminRoutes.Posts)}>
          Back
        </Button>
      }
    >
      <Card variant="outlined">
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={1}
            mb={1.5}
          >
            <PostAuthor
              name={post.author?.name}
              pictureUrl={post.author?.picture_url}
              equippedStoreItems={
                (post.author as any)?.equipped_store_items
              }
              subtitle={dayjs(post.created_at).format("MMM D, YYYY h:mm A")}
              size={40}
            />
            {(post.tags || []).length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                justifyContent="flex-end"
                sx={{ maxWidth: "45%" }}
              >
                {(post.tags || []).map((row) => (
                  <Chip
                    key={row.post_tag_id}
                    label={row.tag.name}
                    size="small"
                    variant="outlined"
                    color={TAG_COLOR(row.tag.type)}
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      opacity: 0.75,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {post.body}
          </Typography>
          {audienceRoleLabel(post.audience_role) && (
            <Box display="flex" justifyContent="flex-end" mt={1.5}>
              <Chip
                label={`Audience: ${audienceRoleLabel(post.audience_role)}`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  opacity: 0.75,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          Add a comment
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          value={answerBody}
          onChange={(e) => setAnswerBody(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder="Write your comment..."
        />
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={handleAnswer}
          disabled={submitting || !answerBody.trim()}
        >
          {submitting ? "Posting..." : "Post Comment"}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        flexWrap="wrap"
        gap={1}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 22, color: "text.secondary" }} />
          <Typography variant="h6">
            Comments ({answers.length})
          </Typography>
        </Stack>
        {answers.length > 0 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={dateSort}
            onChange={(_, value: DateSort | null) => {
              if (value) setDateSort(value);
            }}
            aria-label="Sort comments by date"
          >
            <ToggleButton value="oldest">Oldest first</ToggleButton>
            <ToggleButton value="newest">Newest first</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      <Stack spacing={2}>
        {answers.map((answer) => (
          <Card key={answer.post_id} variant="outlined">
            <CardContent>
              <Box mb={1.5}>
                <PostAuthor
                  name={answer.author?.name}
                  pictureUrl={answer.author?.picture_url}
                  equippedStoreItems={
                    (answer.author as any)?.equipped_store_items
                  }
                  subtitle={dayjs(answer.created_at).format(
                    "MMM D, YYYY h:mm A",
                  )}
                  size={36}
                />
              </Box>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {answer.body}
              </Typography>
            </CardContent>
          </Card>
        ))}
        {answers.length === 0 && (
          <Alert severity="info">No comments yet. Be the first to reply.</Alert>
        )}
      </Stack>
    </MainLayoutContainer>
  );
};
